import React from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconModeDark } from '@components/icons/icon-mode-dark';
import { IconModeLight } from '@components/icons/icon-mode-light';
import { IconJourneyLogoGradientBlue } from '@components/icons/journey-logo-plain-gradient-blue';
import { IconJourneyLogoGradientRed } from '@components/icons/journey-logo-plain-gradient-red';
import { Stack } from '@components/stack';
import { StateGroup } from '@components/state-selector/state-group';
import { Text } from '@components/text';
import { ThemeVariant, useCurrentTheme } from '@components/theme-provider';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useInvalidateAuthentication } from 'api/authentication/hooks';
import { useCompletePreboarding } from 'api/company/hooks';
import { useEditPreboarding } from 'api/preboarding/hooks';

import { PreboardingPage } from './common';

export const CompanyTheme = () => {
  const { t } = useScopedTranslate(locale);
  const { currentTheme, setTheme } = useCurrentTheme();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const editUserMutation = useEditPreboarding({ onSuccess: invalidateCurrentUser });
  const completePreboardingMutation = useCompletePreboarding();

  const handleContinue = async () => {
    const formData = new FormData();
    formData.append('hasFinishedPreboarding', 'true');
    await Promise.all([
      editUserMutation.mutateAsync(formData),
      completePreboardingMutation.mutateAsync(),
    ]);
  };

  return (
    <PreboardingPage>
      <Stack space={2}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: pxToRem(-24),
          }}
        >
          {currentTheme === 'dark' ? (
            <IconJourneyLogoGradientRed size={144} />
          ) : (
            <IconJourneyLogoGradientBlue size={144} />
          )}
        </div>
        <Text text={t('title')} font="heading1" center />
        <div>
          <Text text={t('configure')} font="regular16" center />
          <Text text={t('description')} font="regular16" center color="textSecondary" />
        </div>
        <ToggleContainer className="toggle-container">
          <StateGroup
            onStateSelected={({ id }) => setTheme(id as ThemeVariant)}
            orientation="horizontal"
            selectedStateId={currentTheme}
            stateItems={[
              {
                icon: <IconModeLight />,
                id: 'light',
                label: t('light'),
                orientation: 'horizontal',
              },
              {
                icon: <IconModeDark />,
                id: 'dark',
                label: t('dark'),
                orientation: 'horizontal',
              },
            ]}
          />
        </ToggleContainer>
      </Stack>
      <Button
        variant="primary-blue"
        onClick={() => handleContinue()}
        isDisabled={editUserMutation.isLoading}
      >
        {t('goToJourneys')}
      </Button>
    </PreboardingPage>
  );
};

const ToggleContainer = styled.div({
  margin: `${pxToRem(16)} ${pxToRem(4)} ${pxToRem(4)}`,

  '.state-card': {
    display: 'flex',
    '.state-card-icon-wrapper': {
      display: 'flex',
    },
    '&:nth-child(2)[data-selected="true"]': {
      borderColor: theme.color.red100,
      '*': {
        color: theme.color.red100,
      },
    },
  },
});

const locale = {
  en: {
    title: 'Last but not least',
    configure: 'Configure your experience',
    description: 'Are you on the light or dark side?',
    light: 'Light side',
    dark: 'Dark side',
    goToJourneys: 'Go to Journeys',
  },
};
